import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ErrorCode, PmsType } from '@ffd/api-client';
import { selectIsRemoteAccessEnabled, selectIsUserSiteOffline, selectSiteSettingsPmsType, selectUserSiteId } from '@libs/store';
import { EaglesoftPasswordPromptService } from '@libs/my-account';
import { ToastService } from '@libs/shared-services';
import { ApiHttpErrorResponse, DISABLE_GLOBAL_ERROR_TOASTS } from '@libs/utils';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, Observable, switchMap, throwError } from 'rxjs';

const SITE_ID_URL_PATTERN = /\/v1\/sites\/([^/]+)\//;

@Injectable({ providedIn: 'root' })
export class ApiHttpErrorResponseInterceptorService implements HttpInterceptor {
  private readonly toastService = inject(ToastService);
  private readonly store = inject(Store);
  private readonly eaglesoftPasswordPromptService = inject(EaglesoftPasswordPromptService);

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.handle$(req, next);
  }

  /**
   * On an Eaglesoft credentials rejection the request is held open for the prompt and re-sent
   * unchanged, so the caller gets the real response — no flow needs to know about credentials.
   * Recursive: a password rejected again starts the cycle over, and a dismissal ends it.
   */
  private handle$(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // selectIsRemoteAccessEnabled resolves to false both when the site's remote access is actually
    // blocked and when there is no selected site at all (e.g. FFD Notes routes carry no :siteId).
    // Gating on hasSelectedSite keeps the "unknown" case from being treated as "disabled", which
    // otherwise silently swallows (EMPTY) unrelated errors for any request whose URL contains "sites".
    const hasSelectedSite = !!this.store.selectSignal(selectUserSiteId)();
    const isSiteOffline = this.store.selectSignal(selectIsUserSiteOffline)();
    const isRemoteAccessDisabled = hasSelectedSite && !this.store.selectSignal(selectIsRemoteAccessEnabled)();
    const pmsType = this.store.selectSignal(selectSiteSettingsPmsType);

    const isSiteRelatedRequest = req.url.includes('sites') || req.url.includes('site-admin');

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // A gateway timeout or a dropped connection arrives with no parsed body at all, so this
        // cannot assume `error.error` is an object.
        const errorMessage: string = error.error?.message ?? '';
        const isEaglesoftError = pmsType() === PmsType.EAGLESOFT || errorMessage.toLowerCase().includes(PmsType.EAGLESOFT.toLowerCase());
        const isEaglesoftCredentialsInvalid =
          this.isApiHttpErrorResponse(error) && error.error.code === ErrorCode.EAGLESOFT_CREDENTIALS_INVALID;

        // When the prompt takes over the caller gets the replay, not an error; otherwise fall through.
        const replayed$ = isEaglesoftCredentialsInvalid ? this.promptThenReplay$(req, next, error) : null;
        if (replayed$) {
          return replayed$;
        }

        // Suppress Errors in GET requests for EAGLESOFT sites.
        const disableGlobalErrorToasts = req.context.get(DISABLE_GLOBAL_ERROR_TOASTS) ?? (req.method === 'GET' && isEaglesoftError);
        const showError =
          (!isSiteOffline || (isSiteOffline && !isSiteRelatedRequest)) &&
          (!isRemoteAccessDisabled || (isRemoteAccessDisabled && !isSiteRelatedRequest));

        if (this.isApiHttpErrorResponse(error) && !disableGlobalErrorToasts && showError) {
          this.toastService.error(error.error.message);
        }
        return showError ? throwError(() => error) : EMPTY;
      }),
    );
  }

  /** Returns null when the URL carries no site id, leaving the normal error path in charge. */
  private promptThenReplay$(req: HttpRequest<unknown>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<unknown>> | null {
    const siteId = SITE_ID_URL_PATTERN.exec(req.url)?.[1];
    if (!siteId) {
      return null;
    }

    // A write is being held open here, so the dialog warns that closing it costs the change.
    return this.eaglesoftPasswordPromptService.prompt(siteId, true).pipe(
      switchMap((result) => {
        if (!result?.success) {
          // Dismissed: normal error path, and the unsaved content stays in the editor for a retry.
          return throwError(() => error);
        }

        return this.handle$(req, next);
      }),
    );
  }

  private isApiHttpErrorResponse(error: unknown): error is ApiHttpErrorResponse {
    return (
      error instanceof HttpErrorResponse &&
      error.error?.message !== undefined &&
      error.error?.code !== undefined &&
      error.error.code !== ErrorCode.AUTHENTICATION_FAILED
    );
  }
}
